import { useEffect } from 'react';
import type { TradeForm } from '../../components/trade-form-sections/types';
import type { TradeFormValues } from '../useCreateTradeForm';

/**
 * Cash-matching writeback (LR-344): when the user types into "Net Settlement
 * Converted Input", trade-calculations derives fxRate but the form's fx_rate
 * field is disabled and never receives the new value. Without this a stale
 * fx_rate (from the prior currency change) gets submitted, silently corrupting
 * position.fx_rate on the backend.
 *
 * On exit (user clears the input → isCashMatchingMode=false), the cash-derived
 * fx_rate intentionally persists — the deliberate cash entry shouldn't be
 * silently overwritten just because the input is now empty.
 *
 * The 6dp equality guard is load-bearing: it stops a string↔number round-trip in
 * form state from re-writing the same value forever. Extracted from
 * useCreateTradeForm (LR-495).
 */
export function useCashMatchingFxWriteback({
  form,
  isCashMatchingMode,
  calculatedFxRate,
  fxRate,
}: {
  form: TradeForm;
  isCashMatchingMode: boolean;
  calculatedFxRate: number | undefined;
  fxRate: TradeFormValues['fx_rate'];
}) {
  useEffect(() => {
    if (!isCashMatchingMode) return;
    const calculated = calculatedFxRate;
    if (calculated === undefined || !(calculated > 0)) return;
    const currentNum = typeof fxRate === 'number'
      ? fxRate
      : (typeof fxRate === 'string' ? parseFloat(fxRate) : NaN);
    // Compare at 6dp precision so a string round-trip in form state can't
    // cause an ULP-level flap that would re-write the same value forever.
    const currentRounded = Number.isFinite(currentNum)
      ? Number(currentNum.toFixed(6))
      : NaN;
    if (currentRounded !== calculated) {
      form.setFieldValue('fx_rate', calculated);
    }
  }, [isCashMatchingMode, calculatedFxRate, fxRate, form]);
}
